import {
  Injectable,
  Inject,
  PLATFORM_ID,
  NgZone,
  Renderer2,
  RendererFactory2,
  DOCUMENT,
} from '@angular/core';
import { Observable, Subject, BehaviorSubject, interval, EMPTY } from 'rxjs';
import { switchMap, takeWhile, catchError, tap } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
import { AuthService } from '../auth/auth.service';
import { NotificationService } from './notification.service';

export interface NotificationCounts {
  unread_count: number;
  alerts: number;
  messages: number;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationPollingService {
  private pollingInterval = 30000; // 30 seconds default
  private isPolling = false;
  private pollingSubscription: any = null;
  private visibilityChangeListener: (() => void) | null = null;
  private renderer: Renderer2;
  private countsSubject$ = new BehaviorSubject<NotificationCounts>({
    unread_count: 0,
    alerts: 0,
    messages: 0,
  });

  // Public observable for count updates
  public counts$ = this.countsSubject$.asObservable();

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private authService: AuthService,
    private notificationService: NotificationService,
    private ngZone: NgZone,
    rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document,
  ) {
    // Renderer2 isn't injectable in services; create it from RendererFactory2
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  /**
   * Check if running in browser
   */
  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  /**
   * Check if page is visible (not in background tab)
   */
  private isPageVisible(): boolean {
    if (!this.isBrowser()) {
      return false;
    }
    // Use Angular's DOCUMENT injection token instead of global document
    return !this.document.hidden;
  }

  /**
   * Start polling for notification counts
   * @param intervalMs Polling interval in milliseconds (default: 30000 = 30 seconds)
   */
  startPolling(intervalMs: number = 30000): void {
    if (!this.isBrowser()) {
      return;
    }

    // Don't start if already polling
    if (this.isPolling) {
      return;
    }

    // Don't start if not authenticated
    if (!this.authService.isAuthenticated()) {
      return;
    }

    this.pollingInterval = intervalMs;
    this.isPolling = true;

    // Initial fetch
    this.fetchCounts();

    // Set up polling with visibility check
    this.ngZone.runOutsideAngular(() => {
      this.pollingSubscription = interval(this.pollingInterval)
        .pipe(
          takeWhile(() => this.isPolling),
          switchMap(() => {
            // Only poll if page is visible and user is authenticated
            if (this.isPageVisible() && this.authService.isAuthenticated()) {
              return this.notificationService.getNotifications({ limit: 1, offset: 0 }, true).pipe(
                tap((response) => {
                  this.ngZone.run(() => {
                    this.countsSubject$.next({
                      unread_count: response.unread_count ?? 0,
                      alerts: response.alerts ?? 0,
                      messages: response.messages ?? 0,
                    });
                  });
                }),
                catchError((error) => {
                  // Silently handle errors - don't spam console
                  return EMPTY;
                }),
              );
            }
            return EMPTY;
          }),
        )
        .subscribe();
    });

    // Listen for visibility changes to pause/resume polling using Angular's Renderer2
    if (this.isBrowser()) {
      this.visibilityChangeListener = this.renderer.listen(
        this.document,
        'visibilitychange',
        this.handleVisibilityChange,
      );
    }
  }

  /**
   * Stop polling for notification counts
   */
  stopPolling(): void {
    this.isPolling = false;

    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }

    // Remove visibility change listener using Angular's Renderer2 cleanup
    if (this.visibilityChangeListener) {
      this.visibilityChangeListener();
      this.visibilityChangeListener = null;
    }
  }

  /**
   * Fetch counts immediately (used for initial load and manual refresh)
   */
  fetchCounts(): void {
    if (!this.isBrowser() || !this.authService.isAuthenticated()) {
      return;
    }

    this.notificationService
      .getNotifications({ limit: 1, offset: 0 }, true)
      .pipe(
        catchError((error) => {
          // Silently handle errors
          return EMPTY;
        }),
      )
      .subscribe((response) => {
        this.countsSubject$.next({
          unread_count: response.unread_count ?? 0,
          alerts: response.alerts ?? 0,
          messages: response.messages ?? 0,
        });
      });
  }

  /**
   * Handle visibility change - pause polling when tab is hidden
   */
  private handleVisibilityChange = (): void => {
    // Use Angular's DOCUMENT injection token instead of global document
    if (this.document.hidden) {
      // Tab is hidden - polling will naturally pause due to visibility check
      // No need to do anything, the switchMap will return EMPTY
    } else {
      // Tab is visible again - fetch counts immediately
      this.fetchCounts();
    }
  };

  /**
   * Update polling interval
   */
  setPollingInterval(intervalMs: number): void {
    if (intervalMs < 5000) {
      // Minimum 5 seconds
      intervalMs = 5000;
    }
    this.pollingInterval = intervalMs;

    // Restart polling with new interval if currently polling
    if (this.isPolling) {
      this.stopPolling();
      this.startPolling(intervalMs);
    }
  }

  /**
   * Check if currently polling
   */
  isCurrentlyPolling(): boolean {
    return this.isPolling;
  }
}

